import { useEffect } from "react";
import type { TrailEntry } from "../api/client";

/**
 * The append-only trail, in a panel at the right edge rather than a card at the foot.
 *
 * **The trail is a reference, and a reference does not belong at the bottom of the thing it
 * describes.** It was a card below the determination and below the administrator's record,
 * which put the longest block on each screen furthest from the controls that write to it:
 * apply an override, and the entry proving you applied it is a scroll away past the whole
 * duration ledger. A panel opens beside the work, is read against it, and closes.
 *
 * **It opens closed.** The trail is evidence rather than instruction — nobody arrives at a
 * screen in order to read it — and a panel that takes a third of the width by default would
 * narrow every screen for the majority of visits that never look at it.
 *
 * **The panel is the same list, not a second rendering of it.** Both callers pass their own
 * sentence function and their own idea of which events are departures, because they have
 * different ones: the certification body marks its own departures amber, the administrator
 * marks Complao reaching into a body's file. What is shared is the shape — hash-chained,
 * one line per event, actor and stamp beneath — and that is what lives here.
 *
 * **Escape closes it and the scrim closes it**, the two things `.modal` already answers to
 * on this platform. A drawer that only closes by its own small button is a drawer people
 * leave open.
 */
export function TrailDrawer({
  open,
  onClose,
  entries,
  describe,
  marked,
  title = "Audit trail",
}: {
  open: boolean;
  onClose: () => void;
  entries: TrailEntry[];
  /** The sentence this screen tells about one event. */
  describe: (entry: TrailEntry) => string;
  /** Amber marks a departure. Which events those are is the caller's question. */
  marked: (entry: TrailEntry) => boolean;
  title?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="scrim drawer-scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer-head">
          <h2>{title}</h2>
          <button type="button" className="btn sec sm" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="drawer-body">
          {entries.length === 0 ? (
            <p className="sub">Nothing has been written to this audit&rsquo;s trail yet.</p>
          ) : (
            <ul className="trail">
              {entries.map((entry) => (
                <li key={entry.sequence} className={marked(entry) ? "ov" : undefined}>
                  <b>{describe(entry)}</b>
                  <span className="m">
                    {entry.actor_name ?? "—"} · {stamp(entry.created_at)} ·{" "}
                    {entry.hash.slice(0, 12)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="drawer-foot">
          <p className="sub">
            Append-only. Every entry is hash-chained to the previous and exported with the
            audit file.
          </p>
        </div>
      </aside>
    </div>
  );
}

/**
 * The control that opens it, so both screens name the trail the same way and carry the same
 * count. A number on the button is what tells a reader the panel is worth opening.
 */
export function TrailButton({
  count,
  onOpen,
  label = "Audit trail",
}: {
  count: number;
  onOpen: () => void;
  label?: string;
}) {
  return (
    <button type="button" className="btn sec sm" onClick={onOpen}>
      {label} <span className="mono">({count})</span>
    </button>
  );
}

function stamp(value: string | null | undefined): string {
  return value ? value.slice(0, 16).replace("T", " ") : "—";
}
